import sendOwsAttachmentRequest from './httpRequestHelpers/sendOwsAttachmentRequest';
import prependServiceBaseUrl from '../utils/prependServiceBaseUrl';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type CreateAttachmentResponse from 'owa-service/lib/contract/CreateAttachmentResponse';
import type FileAttachment from 'owa-service/lib/contract/FileAttachment';
import createAttachmentJsonRequest from 'owa-service/lib/factory/createAttachmentJsonRequest';
import createAttachmentRequest from 'owa-service/lib/factory/createAttachmentRequest';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { trace } from 'owa-trace';
import type { ClientItemId } from 'owa-client-ids';
import { getApolloClient } from 'owa-apollo';
import { AddAttachmentToDraftDocument } from '../graphql/__generated__/AddAttachmentToDraftMutation.interface';
import { isFeatureEnabled } from 'owa-feature-flags';
import type AttachmentInfoResponseMessage from 'owa-service/src/contract/AttachmentInfoResponseMessage';

const CREATE_ATTACHMENT_URL = prependServiceBaseUrl('/service.svc/CreateAttachmentFromLocalFile');

export interface SliceInformation {
    sliceNumber: number;
    totalSlices: number;
}

export default async function createAttachmentFromLocalFile(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: Blob,
    onProgressHandler?: (e: ProgressEvent) => void,
    sliceInformation?: SliceInformation
): Promise<CreateAttachmentResponse> {
    trace.info(
        `Attachment: createAttachment Id: ${parentItemId.Id}, ChangeKey: ${parentItemId.ChangeKey}`
    );

    if (isFeatureEnabled('mon-cmp-graphQLAttachments')) {
        const attachmentInput = convertAttachmentTypeIntoGraphQLType(attachment);
        const client = await getApolloClient();
        const result = await client.mutate({
            variables: {
                options: {
                    ParentItemId: parentItemId,
                    Attachment: attachmentInput,
                    CancellationId: cancellationId,
                    attachmentType: 'LocalFile',
                    fileBlob: backingObject,
                    mailboxInfo: parentItemId.mailboxInfo,
                    isInline: attachment.IsInline,
                    sliceInformation,
                },
            },
            mutation: AddAttachmentToDraftDocument,
        });
        const addAtachmentData = result.data.addAttachmentToDraft;

        // There are a lot of subtle dependencies right now on __type being set vs OwsTypeName
        // A future work item for all client code is to remove dependencies on specific OWS related type information
        // As with varying backing services, that information may not be present.
        // We map it back here to try to preserve compatability.
        const mappedAttachments = [
            { ...addAtachmentData.attachment, __type: addAtachmentData.attachment.OwsTypeName },
        ];

        // This typing appears important, else responseMessage will be typed as SingleResponseMessage
        // If we get to this point (i.e. Apollo returns a result), we were successful.
        const responseMessage: AttachmentInfoResponseMessage = {
            Attachments: mappedAttachments,
            IsCancelled: addAtachmentData.isCancelled,
            ResponseClass: 'Success',
            ResponseCode: 'NoError',
        };
        return {
            ResponseMessages: {
                Items: [responseMessage],
            },
        };
    } else {
        const requestBody = createAttachmentRequest({
            ParentItemId: parentItemId,
            Attachments: [attachment],
            RequireImageType: attachment.IsInline,
            IncludeContentIdInResponse: attachment.IsInline,
            ClientSupportsIrm: true,
            CancellationId: cancellationId,
        });

        if (sliceInformation) {
            // If slice information is present then add that information in the request
            requestBody.SliceNumber = sliceInformation.sliceNumber;
            requestBody.TotalSlices = sliceInformation.totalSlices;
        }

        const jsonRequest = createAttachmentJsonRequest({
            Header: getJsonRequestHeader(),
            Body: requestBody,
        });

        const fileAttachment = attachment as FileAttachment;

        const jsonResponse = await sendOwsAttachmentRequest(
            CREATE_ATTACHMENT_URL,
            jsonRequest,
            'CreateAttachmentFromLocalFile',
            parentItemId.mailboxInfo,
            fileAttachment && backingObject ? backingObject : null,
            onProgressHandler
        );

        return jsonResponse.Body;
    }
}

function convertAttachmentTypeIntoGraphQLType(attachment: AttachmentType) {
    // Apollo complains if you provide it a type as an input that doesn't exactly conform to the scehma, even though TS says it's fine.
    // We need to delete the __type information to get things to work.
    const graphQLAttachment = {
        ...attachment,
        OwsTypeName: attachment.__type,
    };

    delete graphQLAttachment.__type;
    return graphQLAttachment;
}
